/**
 * Ball prediction — the shared trajectory every bot reasons about.
 *
 * ===========================================================================
 * WHY THIS IS A TRANSCRIPTION, NOT A MODEL
 * ===========================================================================
 *
 * A bot that plans against an approximation of the ball is planning against a
 * different game. `stepBallFree` below is the ball half of `World.step`:
 * `integrateBall`, then `resolveBallArena` — THE one from src/sim/world, not a
 * copy of it. Nothing is simplified: no analytic parabola, no plane-only arena,
 * no fixed bounce coefficient.
 *
 * The payoff is that the prediction is not merely close to the simulation, it
 * is BIT-IDENTICAL to it for as long as no car touches the ball. That is the
 * headline test in test/bot/prediction.test.ts and it is what lets the decision
 * layer treat "where the ball will be at t" as a fact rather than an estimate.
 *
 * THE ONE IMPORT FROM src/sim. This file used to transcribe the resolution loop
 * and its pass count by hand, which made the exactness above an invariant kept
 * by nothing but a comment: editing the world's ball contact silently made
 * every bot plan against a different game. Two pure symbols are imported
 * instead. Note what is NOT imported — `World` itself. Predicting the ball must
 * not be able to step cars, boost pads, a match clock or a score, and must not
 * allocate a second arena, so the predictor is handed a `BallState` and an
 * `ArenaGeometry` and can reach nothing else.
 *
 * ===========================================================================
 * COST, AND WHY THE CACHE IS SHARED
 * ===========================================================================
 *
 * One 720-tick prediction is ~720 `collideSphere` queries. The BVH answers a
 * ball-sized query in ~0.2 us, so a full prediction is a fraction of a
 * millisecond — cheap ONCE per frame, and six times that if every bot in a 3v3
 * computes its own. So the trajectory is owned by `BallPredictor`, computed at
 * most once per tick, and handed to every bot as a read-only view.
 *
 * Two further economies, both bounded so they can never silently degrade:
 *
 *   - Samples live in ONE Float64Array with a stride of 9 (position, velocity,
 *     angular velocity). Queries read into a caller-owned `BallSample`, so the
 *     per-frame path allocates nothing but the view object itself.
 *   - While the ball is untouched, tick T+1's trajectory is tick T's minus its
 *     first sample. `BallPredictor` reuses the buffer with an offset instead of
 *     recomputing, and refreshes once the offset reaches `refreshTicks` so the
 *     horizon can never shrink below `horizonTicks - refreshTicks`.
 *
 * ===========================================================================
 * INVALIDATION
 * ===========================================================================
 *
 * The reuse above is only sound while the world's ball follows the predicted
 * one, which is exactly "no car touched it and the field was not reset".
 * `observe(events)` takes a world step's event record and invalidates on a
 * touch, a kickoff, a goal or a frozen tick. That is the explicit,
 * unit-testable path.
 *
 * `predict` ALSO compares the live ball against the sample it is about to reuse
 * and recomputes on any mismatch. That backstop is not a substitute for
 * `observe` — it is counted separately as `staleRecomputes`, and a test asserts
 * that a correctly-driven predictor never needs it. A caller that forgets to
 * call `observe` gets correct predictions and a nonzero counter, rather than
 * silently wrong ones.
 */

import { integrateBall, type BallState } from '../physics/ball';
import { DEFAULT_PHYSICS, type ResolvedPhysics } from '../physics/mutators';
import { DT } from '../physics/constants';
import { clone, vec3, type Vec3 } from '../physics/math';
import type { ArenaGeometry } from '../physics/surface';
import { resolveBallArena } from '../sim/world';

/** 6 s at 120 Hz. Long enough for any shot a ground bot plans, short enough
 *  that the trajectory stays meaningful — beyond ~6 s a single bounce error
 *  dominates and the plan is fiction. */
export const PREDICTION_TICKS = 720;

/** Floats per sample: position, velocity, angular velocity. */
const STRIDE = 9;

// ---------------------------------------------------------------------------
// Samples
// ---------------------------------------------------------------------------

/** One tick of predicted ball state. Reused in place by `readInto`. */
export interface BallSample {
  /** Ticks from now. 0 is the state the prediction was seeded with. */
  tick: number;
  /** Seconds from now, i.e. `tick * DT`. */
  time: number;
  position: Vec3;
  velocity: Vec3;
  angularVelocity: Vec3;
}

export const makeSample = (): BallSample => ({
  tick: 0,
  time: 0,
  position: vec3(),
  velocity: vec3(),
  angularVelocity: vec3(),
});

// ---------------------------------------------------------------------------
// The trajectory
// ---------------------------------------------------------------------------

/**
 * A read-only window onto a predicted trajectory.
 *
 * Instances are cheap views over a buffer the predictor owns: `start` is the
 * sample that is "now". Never mutate one, and never hold one past the tick it
 * was issued for — the buffer underneath is reused.
 */
export class BallPrediction {
  /** Number of samples available, counting the one at t = 0. */
  readonly ticks: number;

  constructor(
    private readonly data: Float64Array,
    private readonly start: number,
    total: number,
    /** Absolute bounce indices into `data`, ascending. */
    private readonly bounceIndices: readonly number[],
    /** World tick this trajectory's t = 0 corresponds to. */
    readonly baseTick: number
  ) {
    this.ticks = total - start;
  }

  /** Seconds of trajectory available. */
  get duration(): number {
    return (this.ticks - 1) * DT;
  }

  /** Read sample `tick` (clamped to the available range) into `out`. */
  readInto(tick: number, out: BallSample): BallSample {
    const t = tick < 0 ? 0 : tick >= this.ticks ? this.ticks - 1 : tick | 0;
    const i = (this.start + t) * STRIDE;
    const d = this.data;
    out.tick = t;
    out.time = t * DT;
    out.position.x = d[i];
    out.position.y = d[i + 1];
    out.position.z = d[i + 2];
    out.velocity.x = d[i + 3];
    out.velocity.y = d[i + 4];
    out.velocity.z = d[i + 5];
    out.angularVelocity.x = d[i + 6];
    out.angularVelocity.y = d[i + 7];
    out.angularVelocity.z = d[i + 8];
    return out;
  }

  /** Allocating form of `readInto`, for tests and cold paths. */
  atTick(tick: number): BallSample {
    return this.readInto(tick, makeSample());
  }

  /**
   * State at `t` seconds from now, linearly interpolated between the two
   * bracketing ticks. Interpolation across a bounce tick is meaningless in the
   * same way it is for any sampled trajectory; use `atTick` when the exact
   * bounce matters.
   */
  at(t: number, out: BallSample = makeSample()): BallSample {
    const exact = t / DT;
    const lo = Math.floor(exact);
    if (lo < 0) return this.readInto(0, out);
    if (lo >= this.ticks - 1) return this.readInto(this.ticks - 1, out);

    const f = exact - lo;
    const a = (this.start + lo) * STRIDE;
    const b = a + STRIDE;
    const d = this.data;
    out.tick = lo;
    out.time = t;
    out.position.x = d[a] + (d[b] - d[a]) * f;
    out.position.y = d[a + 1] + (d[b + 1] - d[a + 1]) * f;
    out.position.z = d[a + 2] + (d[b + 2] - d[a + 2]) * f;
    out.velocity.x = d[a + 3] + (d[b + 3] - d[a + 3]) * f;
    out.velocity.y = d[a + 4] + (d[b + 4] - d[a + 4]) * f;
    out.velocity.z = d[a + 5] + (d[b + 5] - d[a + 5]) * f;
    out.angularVelocity.x = d[a + 6] + (d[b + 6] - d[a + 6]) * f;
    out.angularVelocity.y = d[a + 7] + (d[b + 7] - d[a + 7]) * f;
    out.angularVelocity.z = d[a + 8] + (d[b + 8] - d[a + 8]) * f;
    return out;
  }

  /** Predicted position only, written into `out`. Allocation-free. */
  positionAt(t: number, out: Vec3): Vec3 {
    const exact = t / DT;
    const lo = Math.min(Math.max(Math.floor(exact), 0), this.ticks - 1);
    const hi = Math.min(lo + 1, this.ticks - 1);
    const f = hi === lo ? 0 : Math.min(Math.max(exact - lo, 0), 1);
    const a = (this.start + lo) * STRIDE;
    const b = (this.start + hi) * STRIDE;
    const d = this.data;
    out.x = d[a] + (d[b] - d[a]) * f;
    out.y = d[a + 1] + (d[b + 1] - d[a + 1]) * f;
    out.z = d[a + 2] + (d[b + 2] - d[a + 2]) * f;
    return out;
  }

  /** Tick indices, relative to now, at which the ball bounces off the arena. */
  bounceTicks(): number[] {
    const out: number[] = [];
    for (const i of this.bounceIndices) {
      if (i >= this.start) out.push(i - this.start);
    }
    return out;
  }

  /** The first arena bounce in the window, or null if the ball never lands. */
  firstBounce(out: BallSample = makeSample()): BallSample | null {
    for (const i of this.bounceIndices) {
      if (i >= this.start) return this.readInto(i - this.start, out);
    }
    return null;
  }

  /**
   * First time the ball's CENTRE crosses height `z`, in seconds, or null.
   *
   * `direction` restricts the crossing: -1 falling, +1 rising, 0 either. The
   * crossing is interpolated within the tick it happens in, so the answer is
   * finer than the tick grid — which matters, because this is what an aerial's
   * takeoff time is computed from.
   */
  firstTimeAtHeight(z: number, direction: -1 | 0 | 1 = 0, fromTick = 0): number | null {
    const d = this.data;
    let prev = d[(this.start + fromTick) * STRIDE + 2];
    for (let t = fromTick + 1; t < this.ticks; t++) {
      const cur = d[(this.start + t) * STRIDE + 2];
      const rising = cur > prev;
      const crosses = (prev - z) * (cur - z) <= 0 && prev !== cur;
      if (crosses && (direction === 0 || (direction === 1) === rising)) {
        const f = (z - prev) / (cur - prev);
        return (t - 1 + f) * DT;
      }
      prev = cur;
    }
    return null;
  }

  /**
   * Earliest sample the caller can actually get to.
   *
   * `canReach(position, time)` answers "could I be at `position` by `time`?" —
   * the caller supplies the drive model, because only it knows its own car's
   * acceleration, turn radius and boost. Scanning forward and taking the FIRST
   * true is what makes this an interception rather than a chase: the answer is
   * where to meet the ball, not where the ball is.
   *
   * `stepTicks` coarsens the scan; the default of 4 (1/30 s) is finer than any
   * decision the bot makes and a quarter of the cost.
   */
  positionWhenReachable(
    canReach: (position: Vec3, time: number) => boolean,
    options: { stepTicks?: number; fromTick?: number; maxTick?: number; out?: BallSample } = {}
  ): BallSample | null {
    const step = Math.max(1, options.stepTicks ?? 4);
    const out = options.out ?? makeSample();
    const scratch = vec3();
    const end = Math.min(this.ticks, options.maxTick ?? this.ticks);
    for (let t = options.fromTick ?? 0; t < end; t += step) {
      const i = (this.start + t) * STRIDE;
      scratch.x = this.data[i];
      scratch.y = this.data[i + 1];
      scratch.z = this.data[i + 2];
      if (canReach(scratch, t * DT)) return this.readInto(t, out);
    }
    return null;
  }
}

// ---------------------------------------------------------------------------
// Simulation
// ---------------------------------------------------------------------------

/**
 * One tick of free ball flight: exactly what `World.stepBall` does on a tick
 * with no car contact, built from the world's own two halves. Exported so a
 * test can assert the equivalence directly rather than only through a full
 * 720-tick trajectory.
 */
export const stepBallFree = (
  ball: BallState,
  arena: ArenaGeometry,
  dt: number = DT,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): boolean => {
  integrateBall(ball, dt, physics);
  return resolveBallArena(ball, arena, physics);
};

/**
 * One-shot prediction, with no caching. Convenient for tests and for offline
 * analysis; gameplay should go through `BallPredictor` so the work is shared.
 */
export const predict = (
  ball: BallState,
  arena: ArenaGeometry,
  ticks: number = PREDICTION_TICKS,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): BallPrediction => {
  const predictor = new BallPredictor(arena, { horizonTicks: ticks, physics });
  return predictor.predict(ball, 0);
};

// ---------------------------------------------------------------------------
// The cache
// ---------------------------------------------------------------------------

/** The subset of a world step's events that can invalidate a prediction. */
export interface BallPredictionEvents {
  /** Any car-ball contact this tick. */
  ballTouches?: { readonly length: number };
  /** The field was reset to a kickoff. */
  kickoff?: boolean;
  /** The tick was frozen, so the ball was held rather than integrated. */
  frozen?: boolean;
  /** A goal was scored. */
  goal?: unknown;
}

export interface BallPredictorOptions {
  horizonTicks?: number;
  /**
   * How far the reuse offset may grow before the trajectory is recomputed.
   * Bounds the worst-case horizon at `horizonTicks - refreshTicks`.
   */
  refreshTicks?: number;
  /**
   * The world's resolved physics. Pass `world.physics` — omitting it under an
   * active ball mutator silently predicts a differently-sized or
   * differently-bouncy ball than the one in play.
   */
  physics?: ResolvedPhysics;
}

/**
 * Owns one trajectory buffer and decides when it has to be rebuilt.
 *
 * One predictor per world, shared by every bot. It holds no reference to the
 * ball or to any car: `predict` is handed the live ball each tick, which is
 * also what lets the staleness backstop work.
 */
export class BallPredictor {
  readonly horizonTicks: number;
  readonly refreshTicks: number;

  private readonly data: Float64Array;
  private readonly bounceIndices: number[] = [];
  private readonly scratch: BallState;

  /** World tick that `data[0]` describes, or -1 when nothing is cached. */
  private sourceTick = -1;
  private dirty = true;

  /** Full recomputations, for tests and profiling. */
  computations = 0;
  /** Recomputations forced by the staleness backstop rather than by `observe`. */
  staleRecomputes = 0;
  /**
   * Calls to `predict`, cached or not. `computations` says how much work the
   * cache saved; this says whether the SHARING works at all — a director that
   * accidentally predicted per bot would show six of these per tick in a 3v3
   * while `computations` stayed low and hid it.
   */
  queries = 0;

  /**
   * The physics the prediction is run under. It MUST be the world's own
   * `ResolvedPhysics`, not the default: with a ball-size or bounciness mutator
   * active, predicting against unmutated constants makes every bot aim at a
   * ball that does not exist. Defaults to `DEFAULT_PHYSICS` so unmutated
   * callers and tests are unaffected.
   */
  readonly physics: ResolvedPhysics;

  constructor(
    readonly arena: ArenaGeometry,
    options: BallPredictorOptions = {}
  ) {
    this.physics = options.physics ?? DEFAULT_PHYSICS;
    this.horizonTicks = Math.max(2, options.horizonTicks ?? PREDICTION_TICKS);
    this.refreshTicks = Math.max(1, options.refreshTicks ?? 60);
    this.data = new Float64Array(this.horizonTicks * STRIDE);
    this.scratch = { position: vec3(), velocity: vec3(), angularVelocity: vec3() };
  }

  /** Force a rebuild on the next `predict`. */
  invalidate(): void {
    this.dirty = true;
  }

  /**
   * Feed a world step's events. Anything that can move the ball off its
   * predicted path invalidates the cache; everything else leaves it alone,
   * which is the whole point of the cache.
   */
  observe(events: BallPredictionEvents): void {
    if (
      (events.ballTouches !== undefined && events.ballTouches.length > 0) ||
      events.kickoff === true ||
      events.frozen === true ||
      (events.goal !== undefined && events.goal !== null)
    ) {
      this.dirty = true;
    }
  }

  /**
   * The trajectory for `ball` as of `tick`. Cheap on a tick where nothing
   * happened; a full simulation otherwise.
   */
  predict(ball: BallState, tick: number): BallPrediction {
    this.queries++;
    let offset = tick - this.sourceTick;

    if (this.dirty || this.sourceTick < 0 || offset < 0 || offset > this.refreshTicks) {
      this.recompute(ball, tick);
      offset = 0;
    } else if (!this.matchesSample(ball, offset)) {
      // The explicit invalidation missed something. Correct, and say so.
      this.staleRecomputes++;
      this.recompute(ball, tick);
      offset = 0;
    }

    return new BallPrediction(this.data, offset, this.horizonTicks, this.bounceIndices, tick);
  }

  /** Does the cached sample at `offset` still describe the live ball? */
  private matchesSample(ball: BallState, offset: number): boolean {
    const i = offset * STRIDE;
    const d = this.data;
    // 1e-6 uu is far below any meaningful divergence and far above the noise a
    // bit-identical transcription can produce (which is zero).
    return (
      Math.abs(d[i] - ball.position.x) < 1e-6 &&
      Math.abs(d[i + 1] - ball.position.y) < 1e-6 &&
      Math.abs(d[i + 2] - ball.position.z) < 1e-6 &&
      Math.abs(d[i + 3] - ball.velocity.x) < 1e-6 &&
      Math.abs(d[i + 4] - ball.velocity.y) < 1e-6 &&
      Math.abs(d[i + 5] - ball.velocity.z) < 1e-6
    );
  }

  private recompute(ball: BallState, tick: number): void {
    const s = this.scratch;
    s.position.x = ball.position.x;
    s.position.y = ball.position.y;
    s.position.z = ball.position.z;
    s.velocity.x = ball.velocity.x;
    s.velocity.y = ball.velocity.y;
    s.velocity.z = ball.velocity.z;
    s.angularVelocity.x = ball.angularVelocity.x;
    s.angularVelocity.y = ball.angularVelocity.y;
    s.angularVelocity.z = ball.angularVelocity.z;

    this.bounceIndices.length = 0;
    const d = this.data;
    for (let t = 0; t < this.horizonTicks; t++) {
      if (t > 0 && stepBallFree(s, this.arena, DT, this.physics)) this.bounceIndices.push(t);
      const i = t * STRIDE;
      d[i] = s.position.x;
      d[i + 1] = s.position.y;
      d[i + 2] = s.position.z;
      d[i + 3] = s.velocity.x;
      d[i + 4] = s.velocity.y;
      d[i + 5] = s.velocity.z;
      d[i + 6] = s.angularVelocity.x;
      d[i + 7] = s.angularVelocity.y;
      d[i + 8] = s.angularVelocity.z;
    }

    this.sourceTick = tick;
    this.dirty = false;
    this.computations++;
  }
}

/** A `BallState` copy, for callers that need to seed a prediction from a pose. */
export const snapshotBall = (ball: BallState): BallState => ({
  position: clone(ball.position),
  velocity: clone(ball.velocity),
  angularVelocity: clone(ball.angularVelocity),
});
